import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardHeader, CardTitle, CardBody } from '../../ui/Card';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
} from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchBankClients, BankClientData } from '../../../pages/manage_clients/bank_clients/service/bankClientService';
import { loadAllGuardsForExport, GuardData } from '../../../pages/manage_guard/service/guardService';

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid ${theme.colors.light.border.light};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ChartHeader = styled.div`
  margin-bottom: ${theme.spacing[6]};
`;

const MetricsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[8]};
  margin-top: ${theme.spacing[4]};
  flex-wrap: wrap;
`;

const Metric = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.light.background.tertiary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.light};
`;

const MetricValue = styled.span`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  line-height: 1.2;
  margin-bottom: ${theme.spacing[1]};
`;

const MetricLabel = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 380px;
  padding: ${theme.spacing[2]};
`;

interface BankChartsProps {
  showBankGuardsOnly?: boolean;
}

const BankCharts: React.FC<BankChartsProps> = ({ showBankGuardsOnly = false }) => {
  const { companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bankBranchesData, setBankBranchesData] = useState<Array<{ month: string; total: number; average: number }>>([]);
  const [bankGuardsData, setBankGuardsData] = useState<Array<{ bank: string; guards: number }>>([]);
  const [totalBranches, setTotalBranches] = useState(0);
  const [newBranchesThisMonth, setNewBranchesThisMonth] = useState(0);

  useEffect(() => {
    const fetchBankData = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const [bankClients, guards] = await Promise.all([
          fetchBankClients(companyData.id),
          loadAllGuardsForExport(companyData.id),
        ]);

        setTotalBranches(bankClients.length);

        // Calculate new branches this month
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const newBranches = bankClients.filter((client) => {
          if (!client.dateOfRegistration) return false;
          const regDate = client.dateOfRegistration?.toDate ? client.dateOfRegistration.toDate() : new Date(client.dateOfRegistration);
          return regDate.getMonth() === currentMonth && regDate.getFullYear() === currentYear;
        }).length;
        setNewBranchesThisMonth(newBranches);

        // Prepare monthly branch data (last 12 months)
        const monthlyData: Record<string, { total: number; count: number }> = {};
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
          const monthKey = date.toLocaleString('default', { month: 'short' }) + ' 01';
          monthlyData[monthKey] = { total: 0, count: 0 };
        }

        bankClients.forEach((client) => {
          if (client.dateOfRegistration) {
            const regDate = client.dateOfRegistration?.toDate ? client.dateOfRegistration.toDate() : new Date(client.dateOfRegistration);
            const monthKey = regDate.toLocaleString('default', { month: 'short' }) + ' 01';
            if (monthlyData[monthKey]) {
              monthlyData[monthKey].total++;
              monthlyData[monthKey].count++;
            }
          }
        });

        // Calculate average
        const branchesChartData = Object.entries(monthlyData).map(([month, data]) => {
          const avg = Object.values(monthlyData).reduce((sum, d) => sum + d.total, 0) / Object.keys(monthlyData).length;
          return {
            month,
            total: data.total,
            average: Math.round(avg),
          };
        });
        setBankBranchesData(branchesChartData);

        // Calculate guards deployed per bank
        const bankGuardCounts: Record<string, number> = {};
        guards.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.client) {
            const client = deployment.client;
            // Check if it's a bank client
            if (client.type === 'Bank' && client.bankName) {
              const bankName = client.bankName;
              bankGuardCounts[bankName] = (bankGuardCounts[bankName] || 0) + 1;
            }
          }
        });

        const guardsChartData = Object.entries(bankGuardCounts)
          .map(([bank, guards]) => ({ bank, guards }))
          .sort((a, b) => b.guards - a.guards)
          .slice(0, 12); // Top 12 banks
        setBankGuardsData(guardsChartData);
      } catch (error) {
        console.error('Error fetching bank data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBankData();
  }, [companyData]);

  if (loading) {
    return (
      <ChartCard>
        <CardBody>
          <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>Loading chart...</div>
        </CardBody>
      </ChartCard>
    );
  }

  if (showBankGuardsOnly) {
    return (
      <ChartCard>
        <CardBody>
          <ChartHeader>
            <CardTitle>Bank wise Total Guards</CardTitle>
            <MetricsRow>
              <Metric>
                <MetricValue>{bankGuardsData.reduce((sum, item) => sum + item.guards, 0)}</MetricValue>
                <MetricLabel>Total Guards Deployed</MetricLabel>
              </Metric>
              <Metric>
                <MetricValue>{bankGuardsData.length}</MetricValue>
                <MetricLabel>Banks with Guards</MetricLabel>
              </Metric>
            </MetricsRow>
          </ChartHeader>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bankGuardsData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="bank"
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={11}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                  formatter={(value: number) => [`${value} guards`, 'Guards']}
                />
                <Bar
                  dataKey="guards"
                  fill={theme.colors.primary[600]}
                  name="Total Guards"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <CardBody>
        <ChartHeader>
          <CardTitle>Total Branches of Banks</CardTitle>
            <MetricsRow>
              <Metric>
                <MetricValue>{totalBranches}</MetricValue>
                <MetricLabel>Total Branches</MetricLabel>
              </Metric>
              <Metric>
                <MetricValue>{newBranchesThisMonth}</MetricValue>
                <MetricLabel>New Branches of Current Month</MetricLabel>
              </Metric>
            </MetricsRow>
          </ChartHeader>
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={bankBranchesData}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.light.border.light} />
                <XAxis
                  dataKey="month"
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={11}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis
                  stroke={theme.colors.light.text.secondary}
                  tick={{ fill: theme.colors.light.text.secondary }}
                  fontSize={12}
                  domain={[0, 80]}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: theme.colors.white,
                    border: `1px solid ${theme.colors.light.border.medium}`,
                    borderRadius: theme.borderRadius.md,
                    boxShadow: theme.shadows.lg,
                    color: theme.colors.light.text.primary,
                  }}
                  labelStyle={{ color: theme.colors.light.text.primary }}
                />
                <Legend
                  wrapperStyle={{ paddingTop: theme.spacing[4] }}
                  iconType="circle"
                  formatter={(value) => <span style={{ color: theme.colors.light.text.primary }}>{value}</span>}
                />
                <Bar
                  dataKey="total"
                  fill={theme.colors.primary[500]}
                  name="Total Sales"
                  radius={[4, 4, 0, 0]}
                />
                <Line
                  type="monotone"
                  dataKey="average"
                  stroke={theme.colors.primary[300]}
                  strokeWidth={2}
                  name="Average"
                  dot={{ fill: theme.colors.primary[300], r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartContainer>
        </CardBody>
      </ChartCard>
  );
};

export default BankCharts;

